import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { useHasPermission } from '@/hooks/use-has-permission';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useCurrentUserId } from '@/modules/service-desk/shared/employee';
import { mergeWhere } from '@/lib/table-query';
import {
  ApplicationsTable,
  useApplicationsTable,
} from '@/modules/service-desk/shared/components/applications-table';
import { useNocApplications, useNocTypes, useWithdrawNoc } from '../../hooks/use-noc';
import { NocApplication, NocType } from '../../types/noc.types';
import { createMyNocColumns } from '../noc-columns/noc-columns';
import { NocDetailsDialog } from '../noc-details-dialog/noc-details-dialog';

export const NocMyApplications = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userId = useCurrentUserId();
  const canApply = useHasPermission(HRM_PERMISSIONS.NOC_APPLICATION.APPLY);
  const canWithdraw = useHasPermission(HRM_PERMISSIONS.NOC_APPLICATION.WITHDRAW);
  const table = useApplicationsTable();
  const [detailsTarget, setDetailsTarget] = useState<NocApplication | null>(null);
  const [withdrawTarget, setWithdrawTarget] = useState<NocApplication | null>(null);

  const { data: nocTypes = [] as NocType[] } = useNocTypes();
  const { data, isLoading, error } = useNocApplications(
    {
      pageNo: table.pagination.pageIndex + 1,
      pageSize: table.pagination.pageSize,
      filter: mergeWhere({ UserId: userId }, table.columnWhere),
      order: table.order,
    },
    Boolean(userId)
  );
  const { mutateAsync: withdraw } = useWithdrawNoc();

  const columns = useMemo(
    () =>
      createMyNocColumns({
        t,
        nocTypes,
        canApply,
        canWithdraw,
        onViewDetails: setDetailsTarget,
        onEdit: (application) => navigate(`/hr/service-desk/noc/apply?id=${application.ItemId}`),
        onWithdraw: setWithdrawTarget,
      }),
    [t, nocTypes, canApply, canWithdraw, navigate]
  );

  return (
    <>
      <ApplicationsTable
        columns={columns}
        result={data}
        isLoading={isLoading}
        error={error}
        table={table}
        onRowClick={setDetailsTarget}
      />
      <NocDetailsDialog
        application={detailsTarget}
        nocTypes={nocTypes}
        onOpenChange={() => setDetailsTarget(null)}
      />
      <ConfirmationModal
        open={Boolean(withdrawTarget)}
        onOpenChange={(open) => !open && setWithdrawTarget(null)}
        title={t('WITHDRAW_NOC')}
        confirmText={t('WITHDRAW')}
        description={t('ARE_YOU_SURE_TO_WITHDRAW_NOC')}
        onConfirm={() => withdrawTarget && withdraw(withdrawTarget.ItemId)}
      />
    </>
  );
};
